import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import getFailureSeries from "../../../Chart/series/getFailureSeries";
import getInternalGapSerie from "../../../Chart/series/getInternalGapSerie";
import getUnexplainedGapSerie from "../../../Chart/series/getUnexplainedGapSerie";
import getAnalysisFlagSerie from "../../../Chart/series/getAnalysisFlagSerie";
import type { IndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../api/useManualFailureValues";
import getManualFailureSeries from "../../../Chart/series/getManualFailuresSeries";
import { useMetrologyOverrides } from "../../../../../api/useMetrologyOverrides";
import { useIndexedFailureMetadata } from "../../../../../api/useFailureMetadata";
import { useTranslations } from "../../../../../api/useTranslations";
import getInvalidQuantityFlagSerie from "../../../Chart/series/getInvalidQuantityFlagSerie";
import { useDisplayInvalidPoints } from "../../../Analysis/ToggleDisplayInvalidPoints";
import type { FailureValueStatus } from "../../../Chart/PointMetadata";
import type { QuantityIndex, Quantity } from "../../../../../api/useQuantities";

const confirmedSet = new Set<FailureValueStatus>(["CONFIRMED"]);
const useSeries = ({
  project,
  yAxisId,
  setups,
  analyses,
  indexedFailureValues,
  indexedManualFailureValues,
  qoi,
  includeSuspectedFailure,
  actionAllowed,
  quantityIndex,
  publicationFlagDisplayed = false,
}: {
  project: Project;
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  qoi: Quantity | null;
  includeSuspectedFailure: boolean;
  actionAllowed: boolean;
  quantityIndex: QuantityIndex;
  publicationFlagDisplayed: boolean;
}): (
  | Highcharts.SeriesLineOptions
  | Highcharts.SeriesArearangeOptions
  | Highcharts.SeriesAreaOptions
  | Highcharts.SeriesScatterOptions
)[] => {
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const translations = useTranslations();
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);

  const failuresSeries = React.useMemo(
    () =>
      getFailureSeries({
        type: "line",
        failureSerieType: "OBSERVABLE",
        project,
        yAxisId,
        setups,
        analyses,
        indexedFailureValues,
        indexedFailureMetadata,
        qoi,
        startLegendIndex: 2,
        actionAllowed,
        failureValueStatusFilter: confirmedSet,
      }),
    [
      actionAllowed,
      analyses,
      project,
      qoi,
      setups,
      yAxisId,
      indexedFailureValues,
      indexedFailureMetadata,
    ],
  );

  const manualFailuresSeries = React.useMemo(
    () =>
      getManualFailureSeries({
        project,
        yAxisId,
        analyses,
        indexedManualFailureValues,
        qoi,
        startLegendIndex: failuresSeries.length + 1,
        actionAllowed: false,
        type: "line",
        replicateInNavigator: true,
      }),
    [analyses, project, qoi, yAxisId, indexedManualFailureValues, failuresSeries],
  );

  const invalidSeries = React.useMemo(
    () =>
      displayInvalidPoints
        ? null
        : getInvalidQuantityFlagSerie({
            analyses,
            quantities: qoi ? [qoi] : [],
            metrologyOverrides,
          }),
    [analyses, displayInvalidPoints, metrologyOverrides, qoi],
  );

  const areaRangeSeries = React.useMemo(() => {
    const series: Highcharts.SeriesArearangeOptions[] = [];
    if (qoi) {
      series.push(
        getInternalGapSerie({
          yAxisId,
          analyses,
          setups,
          quantity: qoi,
          legendIndex: 0,
          route: "summary",
          metrologyOverrides,
          translations,
        }),
        getUnexplainedGapSerie({
          yAxisId,
          indexedFailureValues,
          indexedFailureMetadata,
          indexedManualFailureValues,
          analyses,
          setups,
          quantity: qoi,
          includeSuspectedFailure,
          legendIndex: 1,
          metrologyOverrides,
          translations,
        }),
      );
    }
    return series;
  }, [
    analyses,
    includeSuspectedFailure,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    metrologyOverrides,
    qoi,
    setups,
    translations,
    yAxisId,
  ]);

  const publicationFlagSeries = React.useMemo(
    () =>
      getAnalysisFlagSerie({
        project,
        setups,
        analyses,
        quantityIndex,
        chartType: "SUMMARY",
        publicationFlagDisplayed,
      }),
    [analyses, quantityIndex, project, setups, publicationFlagDisplayed],
  );

  return React.useMemo(
    () =>
      [
        ...areaRangeSeries,
        ...failuresSeries,
        ...manualFailuresSeries,
        invalidSeries,
        publicationFlagSeries,
      ].filter(s => s !== null),
    [areaRangeSeries, failuresSeries, invalidSeries, manualFailuresSeries, publicationFlagSeries],
  );
};

export default useSeries;
